/**
 * meta_nav.js
 * Single source of truth for navigation button configurations
 *
 * Supports multiple app-specific navigation configs with hierarchical primary/secondary structure
 * Integrated with MicrocopyManager for i18n support
 *
 * Navigation Structure:
 * - Each app has primary navigation items
 * - Each primary item can have secondary (sub-navigation) items
 * - Secondary items have the same schema as primary items
 *
 * Schema:
 * - token: string - Unique identifier (route name)
 * - caption: string - Display text (pulled from MicrocopyManager)
 * - enabled: boolean - Whether the item is active
 * - icon: string - CSS class string for icon (e.g., "fas fa-home")
 * - admin_role: boolean - Requires admin privileges
 * - tagline: string - Verbose description/tooltip (pulled from MicrocopyManager)
 * - secondary: array - Child navigation items (same schema)
 *
 * i18n Integration:
 * - Captions and taglines are retrieved from MicrocopyManager
 * - Token naming convention: nav_{lowercase_token} for captions
 * - Token naming convention: nav_{lowercase_token}_tagline for taglines
 * - Language changes are reflected via I18nController
 *
 * Current: Returns static navigation data
 * Future: Will fetch from coreMeta table for dynamic configuration
 *
 * Usage:
 *   import { NavMetaManager } from './meta_nav.js';
 *
 *   // Set app context
 *   NavMetaManager.setAppToken('client-saas');
 *
 *   // Get primary navigation
 *   const primaryNav = NavMetaManager.getPrimaryNav();
 *
 *   // Get flattened navigation (primary + all secondary)
 *   const allNav = NavMetaManager.getFlattenedNav();
 */

import { MicrocopyManager } from '../meta_microcopy_i18n.js';

// Route access is independent of where a page appears in navigation. Keep these
// existing requirements stable when regrouping tools; public /templates still has
// no router guard. Backend authorization remains authoritative.
const SIDE_NAV_ROUTE_POLICY = {
  '/home': { capability: 'view_dashboard' },
  '/performance': { capabilityAny: ['view_stats', 'view_ad_metrics'] },
  '/magnets': { capability: 'view_magnets' },
  '/magnets/drafts': { capability: 'view_magnets' },
  '/magnets/archived': { capability: 'view_magnets' },
  '/magnets/new': { capability: 'provision_magnets' },
  '/magnets/:guid/edit': { capability: 'manage_magnets' },
  '/magnets/:guid/edit/:section': { capability: 'manage_magnets' },
  '/leads': { capability: 'view_magnets' },
  '/templates': { capabilityAny: ['provision_magnets', 'manage_magnets'] },
  '/conversations': { capability: 'view_conversations' },
  '/ab-testing': { capability: 'manage_magnets' },
  '/analytics': { capability: 'view_stats' },
  '/analytics/traffic': { capability: 'view_stats' },
  '/analytics/ad-performance': { capabilityAny: ['view_ad_metrics', 'connect_ad_accounts'] },
  '/analytics/conversions': { capability: 'view_ad_metrics' },
  '/ad-accounts': { capability: 'connect_ad_accounts' },
  '/clients': { capability: 'create_client_accounts' },
  '/questionnaires': { capabilityAny: ['create_client_accounts', 'manage_deliverables'], uiModes: ['admin', 'reseller'] },
  '/deliverables': { capabilityAny: ['create_client_accounts', 'view_magnets'], uiModes: ['admin', 'reseller', 'managed'] },
  '/team': { capability: 'manage_team' },
  '/billing': { capability: 'view_billing', uiModes: ['self_serve'] },
  '/settings': {},
  '/settings/workspace': {},
  '/settings/notifications': {},
  '/settings/integrations': {},
  '/settings/security': {},
};
const sideItem = (token, caption, icon, route, extra = {}) => ({
  token, caption, icon, route, enabled: true, ...SIDE_NAV_ROUTE_POLICY[route], ...extra,
});

export class NavMetaManager {
  static #appToken = null;

  /**
   * App-specific navigation configurations
   * Future: This will be fetched from coreMeta table
   */
  static #navConfigs = {
    "client-saas": [
      {
        token: "HOME",
        caption: "Home",
        enabled: true,
        icon: "--icon",
        admin_role: false,
        tagline: "Home Landing Page",
        secondary: [
          {
            token: "HOME_APPS",
            caption: "My Apps",
            enabled: true,
            icon: "--icon",
            admin_role: false,
            tagline: "My Apps",
          },
          {
            token: "HOME_DASH",
            caption: "Dashboard",
            enabled: true,
            icon: "--icon",
            admin_role: false,
            tagline: "Dashboard",
          }
        ]
      },
      {
        token: "ENGAGEMENTS",
        caption: "Engagements",
        enabled: true,
        icon: "var(--icon-engagements)",
        admin_role: false,
        tagline: "Customer engagement tools and interactions",
        secondary: [
          {
            token: "ENGAGEMENTS_SURVEYS",
            caption: "Surveys",
            enabled: true,
            icon: "var(--icon-surveys)",
            admin_role: false,
            tagline: "Create and manage customer surveys",
            secondary: []
          },
          {
            token: "ENGAGEMENTS_THUMBS",
            caption: "Feedback",
            enabled: true,
            icon: "var(--icon-feedback)",
            admin_role: false,
            tagline: "Quick thumbs up/down feedback",
            secondary: []
          },
          {
            token: "ENGAGEMENTS_VOICEMAIL",
            caption: "Voicemail",
            enabled: true,
            icon: "var(--icon-voicemail)",
            admin_role: false,
            tagline: "Voice message collection",
            secondary: []
          },
          {
            token: "ENGAGEMENTS_GUIDEDTOURS",
            caption: "Guided Tours",
            enabled: true,
            icon: "var(--icon-guided-tours)",
            admin_role: false,
            tagline: "Interactive product tours",
            secondary: []
          },
          {
            token: "ENGAGEMENTS_SLIDESHOWS",
            caption: "Slideshows",
            enabled: true,
            icon: "var(--icon-slideshows)",
            admin_role: false,
            tagline: "Content slideshows and presentations",
            secondary: []
          },
          {
            token: "ENGAGEMENTS_ABTESTS",
            caption: "A/B Tests",
            enabled: true,
            icon: "var(--icon-ab-tests)",
            admin_role: false,
            tagline: "A/B testing campaigns",
            secondary: []
          },
          {
            token: "ENGAGEMENTS_QUESTIONNAIRES",
            caption: "Questionnaires",
            enabled: true,
            icon: "var(--icon-questionnaires)",
            admin_role: false,
            tagline: "Detailed questionnaires and forms",
            secondary: []
          }
        ]
      },
      {
        token: "DEVELOP",
        caption: "Develop",
        enabled: true,
        icon: "var(--icon-develop)",
        admin_role: false,
        tagline: "Developer resources and tools",
        secondary: [
          {
            token: "DEVELOP_DOCUMENTATION",
            caption: "Documentation",
            enabled: true,
            icon: "var(--icon-documentation)",
            admin_role: false,
            tagline: "API and product documentation",
            secondary: []
          },
          {
            token: "DEVELOP_DEVTOOLS",
            caption: "Dev Tools",
            enabled: true,
            icon: "var(--icon-dev-tools)",
            admin_role: false,
            tagline: "Developer tools and utilities",
            secondary: []
          },
          {
            token: "DEVELOP_KBASE",
            caption: "Knowledge Base",
            enabled: true,
            icon: "var(--icon-knowledge-base)",
            admin_role: false,
            tagline: "Technical knowledge base and articles",
            secondary: []
          },
          {
            token: "DEVELOP_HACKATHON",
            caption: "Hackathon",
            enabled: true,
            icon: "var(--icon-hackathon)",
            admin_role: false,
            tagline: "Hackathon events and challenges",
            secondary: []
          },
          {
            token: "DEVELOP_PARTNER",
            caption: "Partner",
            enabled: true,
            icon: "var(--icon-partner)",
            admin_role: false,
            tagline: "Partner program and integrations",
            secondary: []
          },
          {
            token: "DEVELOP_CERTIFICATION",
            caption: "Certification",
            enabled: true,
            icon: "var(--icon-certification)",
            admin_role: false,
            tagline: "Developer certification programs",
            secondary: []
          }
        ]
      },
      {
        token: "PROFILE",
        caption: "Profile",
        enabled: true,
        icon: "var(--icon-profile)",
        admin_role: false,
        tagline: "Manage your personal profile and account",
        secondary: [
          {
            token: "PROFILE_ABOUTME",
            caption: "About Me",
            enabled: true,
            icon: "var(--icon-about-me)",
            admin_role: false,
            tagline: "View and edit your personal information",
            secondary: []
          },
          {
            token: "PROFILE_BADGES",
            caption: "Badges",
            enabled: true,
            icon: "var(--icon-badges)",
            admin_role: false,
            tagline: "View your earned badges and achievements",
            secondary: []
          },
          {
            token: "PROFILE_LANGUAGE",
            caption: "Language",
            enabled: true,
            icon: "var(--icon-profile-language)",
            admin_role: false,
            tagline: "Manage language settings and preferences",
            secondary: []
          },
          {
            token: "RESET_PASSWORD",
            caption: "Change Password",
            enabled: true,
            icon: "var(--icon-change-password)",
            admin_role: false,
            tagline: "Change your account password",
            secondary: []
          },
          {
            token: "SIGN_OUT",
            caption: "Sign Out",
            enabled: true,
            icon: "var(--icon-sign-out)",
            admin_role: false,
            tagline: "Log out of your account",
            secondary: []
          }
        ]
      },
      {
        token: "ACCOUNT",
        caption: "Account",
        enabled: true,
        icon: "var(--icon-account)",
        admin_role: false,
        tagline: "Account management and support",
        secondary: [
          {
            token: "ACCOUNT_CONTACT",
            caption: "Contact",
            enabled: true,
            icon: "var(--icon-contact)",
            admin_role: false,
            tagline: "Contact support and sales",
            secondary: []
          },
          {
            token: "ACCOUNT_PRICETIER",
            caption: "Price Tier",
            enabled: true,
            icon: "var(--icon-price-tier)",
            admin_role: false,
            tagline: "View and manage pricing plans",
            secondary: []
          },
          {
            token: "ACCOUNT_HELPFAQ",
            caption: "Help FAQ",
            enabled: true,
            icon: "var(--icon-help-faq)",
            admin_role: false,
            tagline: "Frequently asked questions",
            secondary: []
          },
          {
            token: "ACCOUNT_TEAM",
            caption: "Team",
            enabled: true,
            icon: "var(--icon-team)",
            admin_role: false,
            tagline: "Manage team members and roles",
            secondary: []
          },
          {
            token: "ACCOUNT_TERMS",
            caption: "Terms",
            enabled: true,
            icon: "var(--icon-terms)",
            admin_role: false,
            tagline: "Terms of service and legal documents",
            secondary: []
          }
        ]
      }
    ],

    "client-sse-console": [
      {
        token: "HOME",
        caption: "Home",
        enabled: true,
        icon: "fas fa-home",
        admin_role: false,
        tagline: "Admin console home dashboard",
        secondary: []
      },
      {
        token: "ACCOUNTS",
        caption: "Accounts",
        enabled: true,
        icon: "fas fa-user",
        admin_role: false,
        tagline: "User account management",
        secondary: []
      },
      {
        token: "GROUPS",
        caption: "Groups",
        enabled: true,
        icon: "fas fa-users-cog",
        admin_role: false,
        tagline: "Group and role management",
        secondary: []
      },
      {
        token: "QUESTIONNAIRES",
        caption: "Questionnaires",
        enabled: true,
        icon: "fas fa-clipboard-list",
        admin_role: false,
        tagline: "Client kickoff questionnaires — read answers and finalize",
        secondary: []
      },
      {
        token: "LEADS_MANAGE",
        caption: "Leads",
        enabled: true,
        icon: "fas fa-user-plus",
        admin_role: false,
        tagline: "Lead management and tracking",
        secondary: []
      },
      {
        token: "ROLLERS",
        caption: "Magnet Builder",
        enabled: true,
        icon: "fas fa-magnet",
        admin_role: false,
        tagline: "Build and configure magnets (steps, actions, flow)",
        secondary: []
      },
      {
        token: "SYSTEM",
        caption: "System",
        enabled: true,
        icon: "fas fa-server",
        admin_role: false,
        tagline: "System tools — dialog, health, and metering",
        secondary: [
          {
            token: "DIALOG",
            caption: "Dialog",
            enabled: true,
            icon: "fas fa-comment-alt",
            admin_role: false,
            tagline: "Dialog and modal management",
            secondary: []
          },
          {
            token: "MAGNET_HEALTH",
            caption: "Magnet Health",
            enabled: true,
            icon: "fas fa-clipboard-check",
            admin_role: false,
            tagline: "Review magnet configuration risks",
            secondary: []
          },
          {
            token: "HEALTH",
            caption: "Health",
            enabled: true,
            icon: "fas fa-heartbeat",
            admin_role: false,
            tagline: "API health and authentication testing",
            secondary: []
          },
          {
            token: "METER",
            caption: "Meter",
            enabled: true,
            icon: "fas fa-tachometer-alt",
            admin_role: false,
            tagline: "Usage metering and analytics",
            secondary: []
          }
        ]
      },
      {
        token: "APPS",
        caption: "Apps",
        enabled: true,
        icon: "fas fa-mobile-alt",
        admin_role: false,
        tagline: "Subscriber apps (EtherCards)",
        secondary: []
      },
      {
        token: "MESSAGES",
        caption: "Messages",
        enabled: true,
        icon: "fas fa-envelope",
        admin_role: false,
        tagline: "Message management and templates",
        secondary: []
      }
    ],

    "LaunCh_console_applet": [
      {
        token: "CONSOLE_HOME",
        caption: "Console Home",
        enabled: true,
        icon: "fas fa-terminal",
        admin_role: false,
        tagline: "Launch console home dashboard",
        secondary: []
      },
      {
        token: "SYSTEM_MONITOR",
        caption: "System Monitor",
        enabled: true,
        icon: "fas fa-desktop",
        admin_role: false,
        tagline: "Real-time system monitoring and health checks",
        secondary: [
          {
            token: "SYSTEM_MONITOR_CPU",
            caption: "CPU Usage",
            enabled: true,
            icon: "fas fa-microchip",
            admin_role: false,
            tagline: "Monitor CPU performance and load",
            secondary: []
          },
          {
            token: "SYSTEM_MONITOR_MEMORY",
            caption: "Memory",
            enabled: true,
            icon: "fas fa-memory",
            admin_role: false,
            tagline: "Track memory usage and allocation",
            secondary: []
          },
          {
            token: "SYSTEM_MONITOR_NETWORK",
            caption: "Network",
            enabled: true,
            icon: "fas fa-network-wired",
            admin_role: false,
            tagline: "Network traffic and connectivity",
            secondary: []
          }
        ]
      },
      {
        token: "LOGS",
        caption: "Logs",
        enabled: true,
        icon: "fas fa-file-medical-alt",
        admin_role: false,
        tagline: "View system logs and error reports",
        secondary: [
          {
            token: "LOGS_APPLICATION",
            caption: "Application",
            enabled: true,
            icon: "fas fa-code",
            admin_role: false,
            tagline: "Application-level logs",
            secondary: []
          },
          {
            token: "LOGS_SYSTEM",
            caption: "System",
            enabled: true,
            icon: "fas fa-server",
            admin_role: false,
            tagline: "System-level logs",
            secondary: []
          },
          {
            token: "LOGS_SECURITY",
            caption: "Security",
            enabled: true,
            icon: "fas fa-lock",
            admin_role: false,
            tagline: "Security audit logs",
            secondary: []
          }
        ]
      },
      {
        token: "DEPLOYMENTS",
        caption: "Deployments",
        enabled: true,
        icon: "fas fa-rocket",
        admin_role: false,
        tagline: "Manage application deployments",
        secondary: []
      }
    ],

    "Client_Property_Sandbox_Emulator": [
      {
        token: "SANDBOX_HOME",
        caption: "Sandbox Home",
        enabled: true,
        icon: "fas fa-home",
        admin_role: false,
        tagline: "Sandbox testing environment home",
        secondary: []
      },
      {
        token: "API_TESTING",
        caption: "API Testing",
        enabled: true,
        icon: "fas fa-vial",
        admin_role: false,
        tagline: "Test API endpoints and responses",
        secondary: [
          {
            token: "API_TESTING_ENDPOINTS",
            caption: "Endpoints",
            enabled: true,
            icon: "fas fa-plug",
            admin_role: false,
            tagline: "Available API endpoints",
            secondary: []
          },
          {
            token: "API_TESTING_REQUESTS",
            caption: "Requests",
            enabled: true,
            icon: "fas fa-paper-plane",
            admin_role: false,
            tagline: "Send test requests",
            secondary: []
          },
          {
            token: "API_TESTING_HISTORY",
            caption: "History",
            enabled: true,
            icon: "fas fa-history",
            admin_role: false,
            tagline: "Request history and logs",
            secondary: []
          }
        ]
      },
      {
        token: "DATA_SIMULATOR",
        caption: "Data Simulator",
        enabled: true,
        icon: "fas fa-database",
        admin_role: false,
        tagline: "Simulate data scenarios and edge cases",
        secondary: [
          {
            token: "DATA_SIMULATOR_GENERATE",
            caption: "Generate",
            enabled: true,
            icon: "fas fa-magic",
            admin_role: false,
            tagline: "Generate mock data",
            secondary: []
          },
          {
            token: "DATA_SIMULATOR_LOAD",
            caption: "Load",
            enabled: true,
            icon: "fas fa-upload",
            admin_role: false,
            tagline: "Load data fixtures",
            secondary: []
          }
        ]
      },
      {
        token: "SCENARIOS",
        caption: "Scenarios",
        enabled: true,
        icon: "fas fa-project-diagram",
        admin_role: false,
        tagline: "Pre-configured test scenarios",
        secondary: []
      },
      {
        token: "RESET_SANDBOX",
        caption: "Reset Sandbox",
        enabled: true,
        icon: "fas fa-redo",
        admin_role: false,
        tagline: "Reset sandbox to initial state",
        secondary: []
      }
    ]
  };

  /**
   * Side-navigation configs (client-saas left nav).
   *
   * A persona-aware MASTER tree authored once; NavMetaManager.getSideNav() filters
   * it by the user's capabilities (from tenancy.js, server-resolved via /me) so each
   * persona (reseller / managed_client / self-serve / admin) sees only its items —
   * no per-persona duplication. Managed clients lack provisioning capabilities, so
   * those items simply don't appear (per design: hide, don't disable).
   *
   * Item schema (extends the primary-nav schema):
   * - token: string         unique id; i18n key nav_{lowercase}; maps to active-state
   * - caption / tagline: i18n fallbacks (nav_{token} / nav_{token}_tagline)
   * - icon: string          Material Symbols name (demo-faithful)
   * - route: string         Navigo hash route this item navigates to
   * - capability: string    CAPABILITIES value required to SEE the item (null = always)
   * - capabilityAny: array  OR-set alternative to `capability`
   * - enabled / count: as primary nav
   * Optional grouping: a node may be { token, group:true, caption, items:[...] };
   * getSideNav drops a group whose items all filter out. (v1 authored flat, demo-style.)
   *
   * Capability strings mirror packages/shared/src/metadata/tenancy.js CAPABILITIES.
   */
  static #sideNavConfigs = {
    "client-saas": [
      sideItem('PORTFOLIO', 'Portfolio', 'domain', '/home', { portfolioOnly: true, tagline: 'Choose a client' }),
      sideItem('PERFORMANCE', 'Performance', 'monitoring', '/performance', { section: true, showChildren: true, children: [
        sideItem('PERFORMANCE_SUMMARY', 'Summary', null, '/performance'),
        sideItem('ANALYTICS_OVERVIEW', 'Magnet performance', null, '/analytics'),
        sideItem('ANALYTICS_TRAFFIC', 'Website traffic', null, '/analytics/traffic'),
        sideItem('ADVERTISING_PERFORMANCE', 'Advertising', null, '/analytics/ad-performance'),
        sideItem('ADVERTISING_CONVERSIONS', 'Ad conversions', null, '/analytics/conversions'),
      ] }),
      sideItem('LEADS', 'Leads', 'contacts', '/leads', { tagline: 'Review calls and website inquiries' }),
      sideItem('MAGNETS', 'Magnets', 'widgets', '/magnets', { section: true, children: [
        sideItem('MAGNETS_ALL', 'Your magnets', null, '/magnets'),
        sideItem('TEMPLATES', 'Templates', null, '/templates'),
        sideItem('CONVERSATIONS', 'Visitor activity', null, '/conversations', { tagline: 'See how visitors move through your magnets' }),
        sideItem('AB_TESTING', 'A/B tests', null, '/ab-testing', { tagline: 'Compare two versions of a magnet' }),
      ] }),
      { token: 'CLIENT_WORK', group: true, caption: 'Client work', items: [
        sideItem('CLIENTS', 'Clients', 'groups', '/clients'),
        sideItem('QUESTIONNAIRES', 'Questionnaires', 'fact_check', '/questionnaires'),
        sideItem('DELIVERABLES', 'Plans & approvals', 'approval', '/deliverables', { tagline: 'Create, review and approve client plans' }),
      ] },
      { token: 'WORKSPACE_TOOLS', group: true, caption: 'Workspace', items: [
        sideItem('CONNECTIONS', 'Connections', 'hub', '/settings/integrations', { section: true, children: [
          sideItem('SETTINGS_INTEGRATIONS', 'Connected tools', null, '/settings/integrations'),
          sideItem('SETTINGS_NOTIFICATIONS', 'Lead alerts & delivery', null, '/settings/notifications'),
          sideItem('ADVERTISING_ACCOUNTS', 'Ad accounts & tracking', null, '/ad-accounts'),
        ] }),
        sideItem('SETTINGS', 'Settings', 'settings', '/settings/workspace', { section: true, children: [
          sideItem('SETTINGS_WORKSPACE', 'Workspace', null, '/settings/workspace'),
          sideItem('TEAM', 'Team', null, '/team'),
          sideItem('BILLING', 'Plan & usage', null, '/billing'),
        ] }),
      ] },
    ],
  };

  /** Personal pages stay reachable from the existing Account footer, outside client settings. */
  static getAccountNav() {
    return [
      sideItem('SETTINGS_GENERAL', 'My profile', null, '/settings'),
      sideItem('SETTINGS_SECURITY', 'Security', null, '/settings/security'),
    ].map(item => this.#resolveNavI18n(item));
  }

  /**
   * Set the app token for context-aware navigation
   * @param {string} appToken - App identifier (e.g., 'client-saas')
   */
  static setAppToken(appToken) {
    if (!this.#navConfigs[appToken]) {
      console.warn(`[NavMetaManager] Unknown app token: ${appToken}. Available: ${Object.keys(this.#navConfigs).join(', ')}`);
    }
    this.#appToken = appToken;
    console.log(`[NavMetaManager] App token set: ${appToken}`);
  }

  /**
   * Get current app token
   * @returns {string|null} Current app token
   */
  static getAppToken() {
    return this.#appToken;
  }

  /**
   * Resolve i18n tokens for a navigation item
   * Converts token (e.g., "HOME") to microcopy tokens (e.g., "nav_home", "nav_home_tagline")
   * @param {Object} item - Navigation item
   * @returns {Object} Item with resolved caption and tagline
   * @private
   */
  static #resolveI18n(item) {
    if (!item) return item;

    const tokenLower = item.token.toLowerCase();
    const captionToken = `nav_${tokenLower}`;
    const taglineToken = `nav_${tokenLower}_tagline`;

    // Try to get i18n values, fallback to static values
    const caption = MicrocopyManager.get(captionToken) || item.caption;
    const tagline = MicrocopyManager.get(taglineToken) || item.tagline;

    return {
      ...item,
      caption,
      tagline,
      // Recursively resolve secondary items
      secondary: item.secondary ? item.secondary.map(s => this.#resolveI18n(s)) : []
    };
  }

  /**
   * Get primary navigation for current app with i18n resolved
   * @returns {Array} Array of primary navigation items with nested secondary
   */
  static getPrimaryNav() {
    if (!this.#appToken) {
      console.warn('[NavMetaManager] No app token set. Call setAppToken() first.');
      return [];
    }

    const navConfig = this.#navConfigs[this.#appToken] || [];

    // Resolve i18n tokens for all items
    return navConfig.map(item => this.#resolveI18n(item));
  }

  /**
   * Get flattened navigation (all primary + secondary items)
   * @returns {Array} Flattened array of all navigation items
   */
  static getFlattenedNav() {
    const primary = this.getPrimaryNav();
    const flattened = [];

    primary.forEach(item => {
      flattened.push(item);
      if (item.secondary && item.secondary.length > 0) {
        flattened.push(...item.secondary);
      }
    });

    return flattened;
  }

  /**
   * Get navigation item by token
   * @param {string} token - Navigation token
   * @returns {Object|null} Navigation item or null if not found
   */
  static getNavByToken(token) {
    const flattened = this.getFlattenedNav();
    return flattened.find(item => item.token === token) || null;
  }

  /**
   * Get admin-only navigation items
   * @returns {Array} Navigation items requiring admin role
   */
  static getAdminNav() {
    const flattened = this.getFlattenedNav();
    return flattened.filter(item => item.admin_role === true);
  }

  /**
   * Get user-accessible navigation items (non-admin)
   * @returns {Array} Navigation items accessible to regular users
   */
  static getUserNav() {
    const flattened = this.getFlattenedNav();
    return flattened.filter(item => item.admin_role === false);
  }

  /**
   * Get navigation as JSON string (for web component attributes)
   * @returns {string} JSON string of navigation
   */
  static getPrimaryNavJSON() {
    return JSON.stringify(this.getPrimaryNav());
  }

  /**
   * Resolve i18n caption/tagline for a side-nav node (item or group).
   * Same convention as primary nav (nav_{token} / nav_{token}_tagline) but does
   * NOT force a `secondary` array — side-nav grouping is handled in getSideNav.
   * @private
   */
  static #resolveNavI18n(item) {
    if (!item || !item.token) return item;
    const t = item.token.toLowerCase();
    return {
      ...item,
      caption: MicrocopyManager.get(`nav_${t}`) || item.caption,
      tagline: MicrocopyManager.get(`nav_${t}_tagline`) || item.tagline,
    };
  }

  /**
   * Get the persona-aware side navigation for the current app, filtered by the
   * user's capabilities. The master tree (#sideNavConfigs) is authored once;
   * items whose capability gate isn't satisfied are removed. Groups whose items
   * all filter out are dropped.
   *
   * @param {string[]} capabilities - CAPABILITIES the user holds (from /me scope).
   *   Pass [] for an unauthenticated/empty state (only ungated items appear).
   * @param {Object} [opts]
   * @param {string} [opts.uiMode] - active-org uiMode (reseller/managed/self_serve/admin);
   *   gates persona-specific client work and self-serve billing.
   * @returns {Array} Filtered, i18n-resolved side-nav tree.
   */
  static getSideNav(capabilities = [], { uiMode = null, portfolio = false } = {}) {
    const key = this.#sideNavConfigs[this.#appToken] ? this.#appToken : 'client-saas';
    const tree = this.#sideNavConfigs[key] || [];
    const caps = Array.isArray(capabilities) ? capabilities : [];

    const isVisible = (node) => {
      // `enabled` is documented at the top of this file as "whether the item is active"
      // and was never actually read — so setting it to false hid nothing, and a
      // destination could be advertised in the nav while having no page behind it.
      // Absent means enabled, so every existing entry is unaffected.
      if (node.enabled === false) return false;
      if (node.portfolioOnly && !portfolio) return false;
      // Persona gate: an entry listing uiModes shows only in those modes, and
      // FAILS CLOSED when the mode is unknown — a surface scoped this way
      // (Billing, 2026-08-26: self-serve only; agency orgs are billed through
      // their program and see nothing) must never leak by default.
      if (Array.isArray(node.uiModes) && !node.uiModes.includes(uiMode)) return false;
      if (node.capability == null && !node.capabilityAny) return true;
      if (node.capability && !caps.includes(node.capability)) return false;
      if (node.capabilityAny && !node.capabilityAny.some((c) => caps.includes(c))) return false;
      return true;
    };

    const resolveNode = node => {
      if (node.group && Array.isArray(node.items)) {
        if (!isVisible(node)) return null;
        const items = node.items.map(resolveNode).filter(Boolean);
        return items.length ? { ...this.#resolveNavI18n(node), items } : null;
      }
      // A section is a container, not an extra permission requirement. Choose an
      // accessible child as its landing so an ad-only reader never lands on stats.
      if (node.section) {
        const children = (node.children || []).filter(isVisible).map(c => this.#resolveNavI18n(c));
        return children.length ? { ...this.#resolveNavI18n(node), route: children[0].route, children } : null;
      }
      return isVisible(node) ? this.#resolveNavI18n(node) : null;
    };
    return tree.map(resolveNode).filter(Boolean);
  }

  /** Exact, existing route policy; menu nesting and visibility never change access. */
  static getRouteCapability(route) {
    const policy = SIDE_NAV_ROUTE_POLICY[route];
    if (!policy) return null;
    return {
      capability: policy.capability ?? null,
      capabilityAny: policy.capabilityAny ? [...policy.capabilityAny] : null,
      uiModes: policy.uiModes ? [...policy.uiModes] : null,
    };
  }

  static getSideNavJSON(capabilities = [], opts = {}) {
    return JSON.stringify(this.getSideNav(capabilities, opts));
  }

  /**
   * Get all available app tokens
   * @returns {Array<string>} List of app identifiers
   */
  static getAvailableApps() {
    return Object.keys(this.#navConfigs);
  }

  /**
   * Fetch navigation config from coreMeta table
   * Meta token format: NAV_{APP_TOKEN}
   * Example: NAV_SSE_SAAS_CLIENT
   *
   * @param {string} baseUrl - API base URL
   * @returns {Promise<Array>} Array of navigation configurations
   */
  static async fetchFromCoreMeta(baseUrl = '/m5t/v5') {
    if (!this.#appToken) {
      console.warn('[NavMetaManager] No app token set. Using static config.');
      return this.getPrimaryNav();
    }

    try {
      // Construct meta_token: NAV_SSE_SAAS_CLIENT
      const metaToken = `NAV_${this.#appToken.toUpperCase().replace(/-/g, '_')}`;

      const response = await fetch(`${baseUrl}/coreMeta?meta_token=${metaToken}`);

      if (!response.ok) {
        console.warn(`[NavMetaManager] Failed to fetch from coreMeta (${response.status}), using static config`);
        return this.getPrimaryNav();
      }

      const data = await response.json();

      // Extract navigation config from meta_keyval
      if (data.ok && data.data && data.data.meta_keyval) {
        console.log(`[NavMetaManager] Loaded navigation from coreMeta: ${metaToken}`);
        return data.data.meta_keyval;
      }

      console.warn('[NavMetaManager] No data in coreMeta response, using static config');
      return this.getPrimaryNav();

    } catch (error) {
      console.error('[NavMetaManager] Error fetching from coreMeta:', error);
      return this.getPrimaryNav();
    }
  }

  /**
   * Get all navigation configs for all apps
   * @returns {Object} All navigation configurations
   */
  static getAll() {
    return { ...this.#navConfigs };
  }
}

// CommonJS export for backwards compatibility
if (typeof module !== 'undefined' && module.exports) {
  module.exports = { NavMetaManager };
}
